import type { SchemaProperty } from 'types/schema';
import type { PropertyType } from '../components/molecules/PropertyFilter/PropertyFilterMolecule';

/**
 * The conceptual vocabulary for "what kind of thing is this aspect", and the single
 * translation layer into it.
 *
 * Three call sites need to answer that question, and they read TWO different input
 * shapes:
 *
 *   - `AtlasSpherePropertyMolecule` and `atlas-header` read a `SchemaProperty` — the
 *     SCHEMA vocabulary, where a choice aspect is one carrying `metadata.options` and a
 *     numeric one has `type: 'number' | 'integer'`.
 *   - `ContextViewEditorOrganism` reads an aspect-catalog entry typed `PropertyType` —
 *     the FILTER vocabulary (it comes from `buildFilterPropertyOptions`), where a choice
 *     aspect is `type: 'enum'` and a numeric one is `'numeric' | 'urgency'`.
 *
 * Each site's own test was correct FOR ITS OWN INPUT, which is why the divergence
 * survived: `'enum'` and `metadata.options` are both right, and `'numeric'|'urgency'` and
 * `'number'|'integer'` are both right — but they are disjoint sets, so the two sides
 * could disagree about the same underlying aspect. A property carrying `metadata.options`
 * whose filter type resolved to something other than `'enum'` rendered as a chip in the
 * table while the editor offered it text options.
 *
 * Both directions now normalise here, side by side, so the mapping is visible in one
 * place and — see `aspectKind.spec.ts` — testable as an INVARIANT: for every aspect
 * expressible in both vocabularies, the two functions must return the same kind.
 */
export const ASPECT_KINDS = ['date', 'datetime', 'reference', 'number', 'choice', 'text'] as const;
export type AspectKind = (typeof ASPECT_KINDS)[number];

/**
 * Schema vocabulary → `AspectKind`.
 *
 * `rawValue` is optional and used ONLY as a value guard on the numeric branch: a property
 * declared numeric whose stored value is not a number must not claim the `number` kind,
 * or the bar/percent renderers format a non-number. The header has no value in hand and
 * passes nothing, which is exactly why it previously reimplemented this function instead
 * of importing it.
 *
 * Branch order is load-bearing and preserved from the original `resolveAspectKind`: the
 * numeric check runs BEFORE the options check, so a numeric property that also carries
 * `metadata.options` and holds a non-numeric value still resolves `choice`.
 */
export function aspectKindFromSchemaProperty(
  propertyDef: Pick<SchemaProperty, 'type' | 'metadata'> | undefined,
  rawValue?: unknown,
): AspectKind {
  if (propertyDef?.type === 'date' || propertyDef?.type === 'datetime') return propertyDef.type;
  if (propertyDef?.type === 'reference') return 'reference';
  if (propertyDef?.type === 'number' || propertyDef?.type === 'integer') {
    // With no value in hand the declared type is the best available answer; with one, it
    // has to actually be a number.
    if (rawValue === undefined || typeof rawValue === 'number') return 'number';
  }
  if (Array.isArray(propertyDef?.metadata?.options)) return 'choice';
  return 'text';
}

/**
 * Filter vocabulary → `AspectKind`.
 *
 * `'urgency'` maps to `number` because it is a numeric score the editor already offers
 * number formatting for. `'arc'`, `'relationship'`, `'perspective'` and `'boolean'` have
 * no dedicated aspect renderer, so they fall to `text` — the same fallback the schema
 * side reaches for anything it does not recognise.
 */
export function aspectKindFromPropertyType(type: PropertyType | undefined): AspectKind {
  switch (type) {
    case 'date':
    case 'datetime':
      return type;
    case 'reference':
      return 'reference';
    case 'numeric':
    case 'urgency':
      return 'number';
    // The unified-filters-v2 multi-value types (EAINT-908) render as the same
    // chip-based multi-select the table editor already offers `enum` — the
    // closest existing `AspectKind`, same as `'enum'` below.
    case 'enum':
    case 'multi':
    case 'person':
    case 'spheretype':
    case 'spherefamily':
      return 'choice';
    default:
      return 'text';
  }
}

/**
 * `AspectKind` → the header's `FilterType`.
 *
 * The header offers filter affordances, not renderers, and has no filter shape for a
 * choice or a reference — both fall to `text`, which is what it did before. Keeping that
 * collapse HERE rather than in a fourth private resolver is the point: the header no
 * longer decides kind, it only decides what a kind can be filtered as.
 */
export function aspectKindToFilterType(kind: AspectKind): 'text' | 'date' | 'numeric' {
  switch (kind) {
    case 'date':
    case 'datetime':
      return 'date';
    case 'number':
      return 'numeric';
    default:
      return 'text';
  }
}
